import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const gradientCheckpointingComponent: ComponentDef = {
  id: 'gradient-checkpointing',
  name: 'Gradient Checkpointing',
  description: 'Trade compute for memory by recomputing activations during the backward pass.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'n_layers', label: 'Layers', type: 'number', default: 12, min: 1, max: 256, step: 1 },
    {
      name: 'variant',
      label: 'Strategy',
      type: 'select',
      default: 'none',
      options: [
        { label: 'None', value: 'none' },
        { label: 'Every Layer', value: 'every_layer' },
        { label: 'Every √N', value: 'sqrt_n' },
        { label: 'Recompute vs Offload', value: 'recompute_offload' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const n_layers = Number(params.n_layers);
    const variant = String(params.variant);

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);

    if (variant === 'none') {
      // No checkpointing: full forward, store all activations
      g.linear('layer_1', 'x', [d_model, d_model]);
      g.activation('act_1', 'layer_1', 'GELU');
      g.linear('layer_2', 'act_1', [d_model, d_model]);
      g.activation('act_2', 'layer_2', 'GELU');
      g.linear('layer_n', 'act_2', [d_model, d_model]);
      g.cache('all_activations', 'layer_n', [n_layers, seq_len, d_model]);
      g.linear('backward', 'all_activations', [d_model, d_model]);
    } else if (variant === 'every_layer') {
      // Checkpoint every layer: discard activations, recompute all
      g.linear('forward_1', 'x', [d_model, d_model]);
      g.activation('act_1', 'forward_1', 'GELU');
      g.scale('discard_1', 'act_1', 'Discard');
      g.linear('forward_2', 'act_1', [d_model, d_model]);
      g.activation('act_2', 'forward_2', 'GELU');
      g.scale('discard_2', 'act_2', 'Discard');
      g.cache('checkpoints', 'forward_2', [n_layers, seq_len, d_model]);
      g.activation('recompute_1', 'checkpoints', 'Recompute');
      g.activation('recompute_2', 'recompute_1', 'Recompute');
      g.linear('backward', 'recompute_2', [d_model, d_model]);
    } else if (variant === 'sqrt_n') {
      // Checkpoint every √N layers: balanced memory/compute tradeoff
      const sqrtN = Math.max(1, Math.round(Math.sqrt(n_layers)));
      g.linear('forward', 'x', [d_model, d_model]);
      g.activation('act', 'forward', 'GELU');
      g.scale('segment', 'act', `√${n_layers} ≈ ${sqrtN} segments`);
      g.cache('checkpoints', 'segment', [sqrtN, seq_len, d_model]);
      g.activation('recompute', 'checkpoints', 'Recompute segments');
      g.linear('backward', 'recompute', [d_model, d_model]);
      g.metric('memory_saved', 'backward', `${Math.round((1 - 1 / sqrtN) * 100)}% less memory`);
    } else {
      // Recompute vs Offload: choose per-layer whether to recompute or offload to CPU
      g.linear('forward', 'x', [d_model, d_model]);
      g.activation('act', 'forward', 'GELU');
      g.split('policy', 'act', [seq_len, d_model], 'Recompute / Offload');
      g.scale('recompute_path', 'policy', 'Recompute (cheap layers)');
      g.cache('offload_path', 'policy', [seq_len, d_model]);
      g.scale('cpu_offload', 'offload_path', 'CPU Offload (expensive layers)');
      g.concat('merged', ['recompute_path', 'cpu_offload'], [seq_len, d_model]);
      g.linear('backward', 'merged', [d_model, d_model]);
    }

    g.output('out', 'backward');
    return g.build();
  }
};
